// src/pages/InstrumentalistsAdmin.tsx
import LoginModal from "@/components/modals/LoginModal";
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Classroom, Location, User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Box,
    Button,
    Card,
    Flex,
    Group,
    Input,
    Loader,
    MultiSelect,
    ScrollArea,
    Select,
    Skeleton,
    Stack,
    Table,
    Text,
    Title,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
    IconAlertCircle,
    IconDeviceFloppy,
    IconLock,
    IconPencil,
    IconRefresh,
    IconSchool,
    IconUserSearch,
    IconX,
} from "@tabler/icons-react";
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Link } from "react-router-dom";

/* -------------------------------- types/helpers ------------------------------- */

type InstrumentalistRow = {
    user: User;
    instrumentNames: string[];
    locationId: string | null;
    classroomIds: string[]; // list of classroom.id the user is assigned to
    isActive: boolean;
};

const getUserInstrumentNames = (u: User): string[] =>
    Array.isArray(u.instruments)
        ? u.instruments.map((i) => i.name).filter(Boolean)
        : [];

/** Build map: instrumentalistId -> classroomIds[], using Classroom.instrumentalists */
const buildClassroomIndex = (classrooms: Classroom[]) => {
    const idx = new Map<string, string[]>();
    for (const c of classrooms) {
        const cid = c.id;
        const list = c.instrumentalistIds ?? [];
        for (const instrumentalistId of list) {
            const arr = idx.get(instrumentalistId) ?? [];
            arr.push(cid);
            idx.set(instrumentalistId, arr);
        }
    }
    return idx;
};

/* ------------------------------------ page ----------------------------------- */

const InstrumentalistsAdmin: React.FC = () => {
    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
    const fzMed = isMobile ? 12 : 14;

    const session = useContext(SessionContext);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [userHasAccess, setUserHasAccess] = useState(false);

    // data
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [instrumentalists, setInstrumentalists] = useState<
        InstrumentalistRow[] | undefined
    >(undefined);
    const [classrooms, setClassrooms] = useState<Classroom[] | undefined>(
        undefined
    );
    const [locations, setLocations] = useState<Location[] | undefined>(undefined);

    // ui
    const [query, setQuery] = useState("");
    const [assignChoices, setAssignChoices] = useState<Map<string, string[]>>(
        new Map()
    );
    const [assignSubmitting, setAssignSubmitting] = useState<
        Map<string, boolean>
    >(new Map());
    const [locationSelection, setLocationSelection] = useState<
        Map<string, string | null>
    >(new Map());
    const [locationSubmitting, setLocationSubmitting] = useState<
        Map<string, boolean>
    >(new Map());

    // edit-mode: only the clicked row's field becomes editable
    const [editingLocationId, setEditingLocationId] = useState<string | null>(
        null
    );
    const [editingClassroomsId, setEditingClassroomsId] = useState<string | null>(
        null
    );

    // per-row toggle activity loader set
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

    if (!session) return null;

    /* -------------------------------- access gate -------------------------------- */

    useEffect(() => {
        const u = session.user;
        if (
            u?.roles?.includes("ConductionAdmin") ||
            u?.roles?.includes("InstrumentalistLiaison")
        ) {
            setUserHasAccess(true);
            setShowLoginModal(false);
        } else {
            setUserHasAccess(false);
            if (!u) setShowLoginModal(true);
        }
    }, [session.user]);

    /* ---------------------------------- load all --------------------------------- */

    const loadAll = useCallback(async () => {
        if (!session.token) return;
        try {
            setLoading(true);
            setInstrumentalists(undefined);
            setClassrooms(undefined);
            setLocations(undefined);

            const [users, classes, locs] = await Promise.all([
                ConductionAPI.getAllInstrumentalists(session.token),
                ConductionAPI.getAllClassrooms(session.token),
                ConductionAPI.getAllLocations(session.token),
            ]);

            const classroomsSafe = classes ?? [];
            const classIdx = buildClassroomIndex(classroomsSafe);

            const locationsSafe = (locs ?? []).sort((a, b) =>
                (a.displayName ?? "").localeCompare(b.displayName ?? "")
            );

            const rows: InstrumentalistRow[] = (users ?? []).map((u) => ({
                user: u,
                instrumentNames: getUserInstrumentNames(u),
                locationId: u.location?.id ?? null,
                classroomIds: classIdx.get(u.id) ?? [],
                isActive: !!u.isActiveInstrumentalist,
            }));

            // Set initial selections for location and classrooms
            setLocationSelection(
                new Map(rows.map((r) => [r.user.id, r.locationId ?? null]))
            );
            setAssignChoices(
                new Map(rows.map((r) => [r.user.id, Array.from(r.classroomIds)]))
            );

            setInstrumentalists(rows);
            setClassrooms(classroomsSafe);
            setLocations(locationsSafe);
        } catch (err) {
            console.error("Error loading instrumentalists/classrooms:", err);
            Utils.displayNotification(
                "Load Failure",
                "Unable to load instrumentalists or classrooms. Please try again.",
                "red",
                <IconAlertCircle size={16} />
            );
            setInstrumentalists([]);
            setClassrooms([]);
            setLocations([]);
        } finally {
            setLoading(false);
        }
    }, [session.token]);

    useEffect(() => {
        void loadAll();
    }, [loadAll]);

    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        await loadAll();
        setRefreshing(false);
    }, [loadAll]);

    /* ----------------------------- memoized lookups/options ---------------------------- */

    const classroomOptions = useMemo(
        () => (classrooms ?? []).map((c) => ({ value: c.id, label: c.name })),
        [classrooms]
    );

    const locationOptions = useMemo(
        () =>
            (locations ?? []).map((l) => ({
                value: l.id,
                label: l.displayName ?? "",
            })),
        [locations]
    );

    const classroomNameById = useMemo(() => {
        const m = new Map<string, string>();
        (classrooms ?? []).forEach((c) => m.set(c.id, c.name));
        return m;
    }, [classrooms]);

    const locationNameById = useMemo(() => {
        const m = new Map<string, string>();
        (locations ?? []).forEach((l) => m.set(l.id, l.displayName ?? ""));
        return m;
    }, [locations]);

    /* ----------------------------- assign / unassign ---------------------------- */

    const submitAssign = async (userId: string) => {
        if (!session.token) return;
        if (!assignChoices.has(userId)) return;
        if (assignSubmitting.get(userId)) return;
        if (!instrumentalists?.find((r) => r.user.id === userId)) return;

        setAssignSubmitting((prev) => new Map(prev).set(userId, true));

        const toAssign = assignChoices.get(userId) ?? [];

        // optimistic UI: add classroomIds
        setInstrumentalists((prev) =>
            (prev ?? []).map((r) =>
                r.user.id !== userId
                    ? r
                    : { ...r, classroomIds: Array.from(new Set([...toAssign])) }
            )
        );

        try {
            await ConductionAPI.setInstrumentalistClassrooms(
                userId,
                toAssign,
                session.token
            );
            Utils.displayNotification(
                "Assignments Saved",
                "Instrumentalist has been assigned to the selected classroom(s).",
                "green",
                <IconSchool size={16} />
            );
            setEditingClassroomsId(null);
        } catch (err) {
            console.error("Assign error:", err);
            Utils.displayNotification(
                "Assign Failed",
                err instanceof Error
                    ? err.message
                    : "An error occurred assigning the instrumentalist.",
                "red",
                <IconX size={16} />
            );
            void loadAll(); // rollback by reload
        } finally {
            setAssignSubmitting((prev) => new Map(prev).set(userId, false));
        }
    };

    const cancelAssign = (userId: string, currentIds: string[]) => {
        setAssignChoices((prev) => {
            const next = new Map(prev);
            next.set(userId, [...currentIds]);
            return next;
        });
        setEditingClassroomsId(null);
    };

    const submitLocation = async (userId: string) => {
        if (!session.token) return;
        if (!locationSelection.has(userId)) return;
        if (locationSubmitting.get(userId)) return;
        if (!instrumentalists?.find((r) => r.user.id === userId)) return;

        setLocationSubmitting((prev) => new Map(prev).set(userId, true));

        const toSet = locationSelection.get(userId) ?? null;

        const loc = locations?.find((l) => l.id === toSet) ?? undefined;
        // optimistic UI: set location
        setInstrumentalists((prev) =>
            (prev ?? []).map((r) =>
                r.user.id !== userId ? r : { ...r, user: { ...r.user, location: loc } }
            )
        );
        try {
            await ConductionAPI.updateUserLocation(userId, toSet, session.token);
            Utils.displayNotification(
                "Location Saved",
                "Instrumentalist's location has been updated.",
                "green",
                <IconDeviceFloppy size={16} />
            );
            setEditingLocationId(null);
        } catch (err) {
            console.error("Set location error:", err);
            Utils.displayNotification(
                "Update Failed",
                err instanceof Error
                    ? err.message
                    : "An error occurred updating the location.",
                "red",
                <IconX size={16} />
            );
            void loadAll(); // rollback by reload
        } finally {
            setLocationSubmitting((prev) => new Map(prev).set(userId, false));
        }
    };

    const cancelLocation = (userId: string, currentId: string | null) => {
        setLocationSelection((prev) => {
            const next = new Map(prev);
            next.set(userId, currentId ?? null);
            return next;
        });
        setEditingLocationId(null);
    };

    /* -------------------------------- toggle active ----------------------------- */

    const toggleActive = async (row: InstrumentalistRow) => {
        if (!session.token) return;
        const id = row.user.id;
        const next = !row.isActive;

        setTogglingIds((prev) => new Set(prev).add(id));

        // optimistic toggle
        setInstrumentalists((prev) =>
            (prev ?? []).map((r) => (r.user.id === id ? { ...r, isActive: next } : r))
        );

        try {
            await ConductionAPI.setInstrumentalistActiveStatus(
                id,
                next,
                session.token
            );
        } catch (err) {
            console.error("Toggle active error:", err);
            // rollback
            setInstrumentalists((prev) =>
                (prev ?? []).map((r) =>
                    r.user.id === id ? { ...r, isActive: !next } : r
                )
            );
            Utils.displayNotification(
                "Update Failed",
                err instanceof Error ? err.message : "Could not update active status.",
                "red",
                <IconAlertCircle size={16} />
            );
        } finally {
            setTogglingIds((prev) => {
                const c = new Set(prev);
                c.delete(id);
                return c;
            });
        }
    };

    /* --------------------------------- filtering -------------------------------- */

    const queryLower = query.trim().toLowerCase();
    const filteredRows = useMemo(() => {
        if (!queryLower || !instrumentalists) return instrumentalists ?? [];
        return instrumentalists.filter((r) => {
            const u = r.user;
            const full = `${u.firstName ?? ""} ${u.lastName ?? ""}`.toLowerCase();
            const username = (u.username ?? "").toLowerCase();
            const email = (u.email ?? "").toLowerCase();
            const instruments = r.instrumentNames.join(" ").toLowerCase();
            return (
                full.includes(queryLower) ||
                username.includes(queryLower) ||
                email.includes(queryLower) ||
                instruments.includes(queryLower)
            );
        });
    }, [instrumentalists, queryLower]);

    /* ----------------------------------- view ----------------------------------- */

    const skeletonRow = (
        <Table.Tr>
            {Array.from({ length: isMobile ? 4 : 6 }).map((_, i) => (
                <Table.Td key={i}>
                    <Skeleton height={18} />
                </Table.Td>
            ))}
        </Table.Tr>
    );

    return (
        !userHasAccess ? (
            <>
                <LoginModal showModal={showLoginModal} setShowModal={setShowLoginModal} />
                <Card p={20}>
                    <IconLock size={48} color={theme.colors.red[6]} />
                    <Text>You do not have permission to access this page.</Text>
                    <Text>
                        Please log into an account with access or <Link to="/">click here to navigate away</Link>.
                    </Text>
                </Card>
            </>
        ) : (
            <Box
                w="100%"
                h="100%"
                p="xs"
                // Parent: header + content (content is height-bounded)
                style={{
                    display: "grid",
                    gridTemplateRows: "auto 1fr",
                    overflow: "hidden",
                    minHeight: 0,
                }}
            >
                {/* Header */}
                <Title
                    p="xs"
                    size={30}
                    c={theme.colors.blue[0]}
                    ta="center"
                >
                    Instrumentalists
                </Title>

                {/* Content card fills remaining height and contains its own scroll window */}
                <Card
                    w="98%"
                    bg={theme.colors.blue[8]}
                    withBorder={false}
                    radius="lg"
                    p={isMobile ? "sm" : "xl"}
                    style={{
                        justifySelf: "center",
                        alignSelf: "stretch",
                        display: "grid",
                        gridTemplateRows: "auto 1fr", // toolbar, then scroll area
                        overflow: "hidden",
                        minHeight: 0,
                    }}
                >
                    {/* Toolbar / filters */}
                    <Group justify="space-between" align="center" mb="md" wrap="wrap">
                        <Group>
                            <Input
                                leftSection={<IconUserSearch size={16} />}
                                placeholder="Search by name, username, email, instrument..."
                                value={query}
                                onChange={(e) => setQuery(e.currentTarget.value)}
                                w={isMobile ? 260 : 380}
                                styles={{ input: { background: theme.colors.blue[0] } }}
                            />
                        </Group>
                        <Button
                            leftSection={refreshing ? <Loader size="xs" /> : <IconRefresh size={16} />}
                            onClick={handleRefresh}
                            variant="default"
                            c={theme.colors.blue[8]}
                        >
                            Refresh
                        </Button>
                    </Group>

                    {/* Scroll window */}
                    <Box
                        bg={theme.colors.blue[0]}
                        style={{
                            borderRadius: 16,
                            overflow: "hidden",
                            minHeight: 0,
                            display: "grid",
                            gridTemplateRows: "1fr",
                        }}
                    >
                        <ScrollArea
                            h="100%" // track has definite height via grid
                            type="auto"
                            viewportProps={{ style: { height: "100%" } }}
                            styles={{ viewport: { overflowX: "hidden" } }}
                        >
                            <Table
                                striped
                                highlightOnHover
                                stickyHeader
                                withTableBorder={false}
                                withColumnBorders={false}
                                verticalSpacing="sm"
                                horizontalSpacing="md"
                                style={{ tableLayout: "auto" }}
                            >
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th w="250px">
                                            <Text fw={700} fz={fzMed}>Instrumentalist</Text>
                                        </Table.Th>
                                        {!isMobile && (
                                            <Table.Th w="300px">
                                                <Text fw={700} fz={fzMed}>Email</Text>
                                            </Table.Th>
                                        )}
                                        <Table.Th>
                                            <Text fw={700} fz={fzMed}>Instruments</Text>
                                        </Table.Th>
                                        <Table.Th w="100px">
                                            <Text fw={700} fz={fzMed}>Active</Text>
                                        </Table.Th>
                                        <Table.Th w="220px">
                                            <Text fw={700} fz={fzMed}>Location</Text>
                                        </Table.Th>
                                        <Table.Th w="260px">
                                            <Text fw={700} fz={fzMed}>Classrooms</Text>
                                        </Table.Th>
                                    </Table.Tr>
                                </Table.Thead>

                                <Table.Tbody>
                                    {loading && (
                                        <>
                                            {React.cloneElement(skeletonRow, { key: "s1" })}
                                            {React.cloneElement(skeletonRow, { key: "s2" })}
                                            {React.cloneElement(skeletonRow, { key: "s3" })}
                                        </>
                                    )}

                                    {!loading && filteredRows.length === 0 && (
                                        <Table.Tr>
                                            <Table.Td colSpan={6}>
                                                <Text ta="center" fw={500}>
                                                    No instrumentalists found.
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    )}

                                    {!loading &&
                                        filteredRows.map((r) => {
                                            const u = r.user;

                                            const busy = togglingIds.has(u.id);
                                            const activeColor = r.isActive ? "green" : "red";
                                            const activeLabel = r.isActive ? "Yes" : "No";

                                            const selectedLoc = locationSelection.get(u.id) ?? null;
                                            const currentLocId = u.location?.id ?? null;
                                            const noLocChange = selectedLoc === currentLocId;

                                            const selectedClasses = assignChoices.get(u.id) ?? [];
                                            const noClassChange =
                                                r.classroomIds.length === selectedClasses.length &&
                                                r.classroomIds.every((c) => selectedClasses.includes(c));

                                            return (
                                                <Table.Tr key={u.id}>
                                                    <Table.Td>
                                                        <Stack gap={2}>
                                                            <Text fw={600}>{u.username}</Text>
                                                            <Text fz="sm" c="dimmed">
                                                                {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                                                            </Text>
                                                        </Stack>
                                                    </Table.Td>

                                                    {!isMobile && (
                                                        <Table.Td>
                                                            <Group>
                                                                <Text fz="sm">{u.email || "—"}</Text>
                                                                {!u.emailConfirmed && (
                                                                    <Tooltip label="Email not verified">
                                                                        <IconAlertCircle size={16} color="orange" />
                                                                    </Tooltip>
                                                                )}
                                                            </Group>
                                                        </Table.Td>
                                                    )}

                                                    <Table.Td>
                                                        <Group gap={6} wrap="wrap">
                                                            {r.instrumentNames.length > 0 ? (
                                                                r.instrumentNames.map((name) => (
                                                                    <Badge key={name} variant="light">
                                                                        {name}
                                                                    </Badge>
                                                                ))
                                                            ) : (
                                                                <Text fz="sm" c="dimmed">No instruments</Text>
                                                            )}
                                                        </Group>
                                                    </Table.Td>

                                                    {/* Active toggle */}
                                                    <Table.Td>
                                                        <Tooltip label={busy ? "Updating..." : "Click to toggle"}>
                                                            <Badge
                                                                variant="outline"
                                                                color={activeColor}
                                                                onClick={() => !busy && void toggleActive(r)}
                                                                style={{ cursor: busy ? "default" : "pointer", userSelect: "none" }}
                                                                leftSection={busy ? <Loader size="xs" /> : undefined}
                                                            >
                                                                {activeLabel}
                                                            </Badge>
                                                        </Tooltip>
                                                    </Table.Td>

                                                    {/* Location editor */}
                                                    <Table.Td>
                                                        {editingLocationId === u.id ? (
                                                            <Flex justify="space-between" align="center" gap={6}>
                                                                <Select
                                                                    flex={1}
                                                                    data={locationOptions}
                                                                    value={selectedLoc}
                                                                    onChange={(value) =>
                                                                        setLocationSelection((prev) => {
                                                                            const next = new Map(prev);
                                                                            next.set(u.id, value);
                                                                            return next;
                                                                        })
                                                                    }
                                                                    searchable
                                                                    clearable
                                                                    nothingFoundMessage="No locations found"
                                                                />
                                                                <Stack gap={0}>
                                                                    <Tooltip label="Save">
                                                                        <Button
                                                                            size="compact-xs"
                                                                            onClick={() => void submitLocation(u.id)}
                                                                            loading={locationSubmitting.get(u.id) || false}
                                                                            disabled={noLocChange}
                                                                            variant="subtle"
                                                                        >
                                                                            <IconDeviceFloppy size={16} />
                                                                        </Button>
                                                                    </Tooltip>
                                                                    <Tooltip label="Cancel">
                                                                        <Button
                                                                            size="compact-xs"
                                                                            variant="subtle"
                                                                            onClick={() => cancelLocation(u.id, currentLocId)}
                                                                        >
                                                                            <IconX size={16} />
                                                                        </Button>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Flex>
                                                        ) : (
                                                            <Flex gap={6} wrap="wrap" justify="space-between">
                                                                <Group flex={1}>
                                                                    <Badge
                                                                        variant="gradient"
                                                                        gradient={{
                                                                            from: locations?.find((loc) => loc.id === currentLocId)?.primaryColor || "blue",
                                                                            to: locations?.find((loc) => loc.id === currentLocId)?.secondaryColor || "blue",
                                                                            deg: 45,
                                                                        }}
                                                                    >
                                                                        {currentLocId ? locationNameById.get(currentLocId) ?? "—" : "—"}
                                                                    </Badge>
                                                                </Group>
                                                                <Button
                                                                    size="compact-xs"
                                                                    variant="subtle"
                                                                    leftSection={<IconPencil size={14} />}
                                                                    onClick={() => {
                                                                        setLocationSelection((prev) => {
                                                                            const next = new Map(prev);
                                                                            next.set(u.id, currentLocId ?? null);
                                                                            return next;
                                                                        });
                                                                        setEditingLocationId(u.id);
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                            </Flex>
                                                        )}
                                                    </Table.Td>

                                                    {/* Classrooms editor */}
                                                    <Table.Td>
                                                        {editingClassroomsId === u.id ? (
                                                            <Flex justify="space-between" align="center" gap={6}>
                                                                <MultiSelect
                                                                    flex={1}
                                                                    data={classroomOptions}
                                                                    value={selectedClasses}
                                                                    onChange={(value) =>
                                                                        setAssignChoices((prev) => {
                                                                            const next = new Map(prev);
                                                                            next.set(u.id, Array.from(value));
                                                                            return next;
                                                                        })
                                                                    }
                                                                    searchable
                                                                    clearable
                                                                    nothingFoundMessage="No classrooms found"
                                                                />
                                                                <Stack gap={0}>
                                                                    <Tooltip label="Save">
                                                                        <Button
                                                                            size="compact-xs"
                                                                            onClick={() => void submitAssign(u.id)}
                                                                            loading={assignSubmitting.get(u.id) || false}
                                                                            disabled={noClassChange}
                                                                            variant="subtle"
                                                                        >
                                                                            <IconDeviceFloppy size={16} />
                                                                        </Button>
                                                                    </Tooltip>
                                                                    <Tooltip label="Cancel">
                                                                        <Button
                                                                            size="compact-xs"
                                                                            variant="subtle"
                                                                            onClick={() => cancelAssign(u.id, r.classroomIds)}
                                                                        >
                                                                            <IconX size={16} />
                                                                        </Button>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Flex>
                                                        ) : (
                                                            <Flex gap={6} justify="space-between" align="center">
                                                                <Group gap={6} wrap="wrap" flex={1}>
                                                                    {r.classroomIds.length > 0 ? (
                                                                        r.classroomIds.map((cid) => (
                                                                            <Badge key={cid} variant="light">
                                                                                {classroomNameById.get(cid) ?? "—"}
                                                                            </Badge>
                                                                        ))
                                                                    ) : (
                                                                        <Badge variant="light">—</Badge>
                                                                    )}
                                                                </Group>
                                                                <Button
                                                                    size="compact-xs"
                                                                    variant="subtle"
                                                                    leftSection={<IconPencil size={14} />}
                                                                    onClick={() => {
                                                                        setAssignChoices((prev) => {
                                                                            const next = new Map(prev);
                                                                            next.set(u.id, [...r.classroomIds]);
                                                                            return next;
                                                                        });
                                                                        setEditingClassroomsId(u.id);
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                            </Flex>
                                                        )}
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    </Box>
                </Card>
            </Box>
        )
    );
};

export default InstrumentalistsAdmin;
